
/**
 * isNull()
 * @param o
 * @returns {boolean}
 */
export function isNull(o) {
  return o === null;
}

/**
 * isUndefined()
 * @param o
 * @returns {boolean}
 */
export function isUndefined(o) {
  return o === undefined;
}

/**
 * isNullOrUndefined()
 * @param o
 * @returns {boolean}
 */
export function isNullOrUndefined(o) {
  return isNull(o) || isUndefined(o);
}

/**
 * isDefined()
 * @param o
 * @returns {boolean}
 */
export function isDefined(o) {
  return !isNullOrUndefined(o);
}

/**
 * isObject()
 * @param o
 * @returns {boolean}
 */
export function isObject(o) {
  if (isDefined(o)) return typeof o === 'object';
  return false;
}

export function _isObject(value) {
  var type = typeof value;
  return !!value && (type == 'object' || type == 'function');
}

/**
 * isFunction()
 * @param f
 * @returns {boolean}
 */
export function isFunction(f) {
  return isDefined(f) && typeof f === 'function';
}

/**
 * isNumber()
 * @param o
 * @returns {boolean}
 */
export function isNumber(o) {
  return !isNaN(parseFloat(o)) && isFinite(o);
}

/**
 * isDecimal()
 * @param o
 * @returns {*|boolean}
 */
export function isDecimal(o) {
  return isNumber(o) && o % 1 !== 0;
}

/**
 * isString
 * @param o
 * @returns {boolean}
 */
export function isString(o) {
  return isDefined(o) && typeof o === 'string';
}

/**
 * isEmptyString()
 * @param o
 * @returns {boolean}
 */
export function isEmptyString(o) {
  if (isString(o)) {
    return o === '';
  }

  return false;
}

/**
 * isStringWithLength()
 * @param o
 * @returns {boolean}
 */
export function isStringWithLength(o) {
  return isString(o) && o.length > 0;
}

/**
 * camelCase()
 * @param o
 * @returns {string}
 */
export function camelCase(o) {
  if (isStringWithLength(o)) {
    return o
      .replace(/\s(.)/g, $1 => {
        return $1.toUpperCase();
      })
      .replace(/\s/g, '')
      .replace(/^(.)/, $1 => {
        return $1.toLowerCase();
      });
  }

  return o;
}

/**
 * isArray()
 * @param o
 * @returns {boolean}
 */
export function isArray(o) {
  return isDefined(o) && Array.isArray(o);
}

/**
 * isArrayWithLength()
 * @param o
 * @returns {boolean}
 */
export function isArrayWithLength(o) {
  return isArray(o) && o.length > 0;
}

/**
 * isTrue()
 * @param o
 * @returns {boolean}
 */
export function isTrue(o) {
  return isDefined(o) && typeof o === 'boolean' && o === true;
}

/**
 * isFalse()
 * @param o
 * @returns {boolean}
 */
export function isFalse(o) {
  return isDefined(o) && typeof o === 'boolean' && o === false;
}

/**
 * isBoolean()
 * @param o
 * @returns {boolean}
 */
export function isBoolean(o) {
  return isTrue(o) || isFalse(o);
}

/**
 * hasOwnProperty()
 * @param o
 * @param prop
 * @returns {boolean|Object}
 */
export function hasOwnProperty(o, prop) {
  return (
    isDefined(o) &&
    (o.hasOwnProperty(prop) || Object.getOwnPropertyDescriptor(o, prop))
  );
}

/**
 * init()
 * @param obj
 * @param prop
 * @param value
 * @returns {undefined}
 */
export function init(obj, prop, value = undefined) {
  return hasOwnProperty(obj, prop) ? obj[prop] : value;
}

export function difference(a: any[], b: any[]): any[] {
  return a.filter(x => !b.includes(x));
}

/**
 * diff
 * @param a
 * @param b
 * @returns []
 */
export function isDiff(a: Object, b: Object) {
  let r = {};
  Object.entries(a).forEach(([k, v]) => {
    if (b[k] === v) return;
    // but what if it returns an empty object? still attach?
    r[k] = _isObject(v) ? difference(v as any, b[k]) : v;
  });
  return r;
}

/**
 * isDate()
 * @param o
 * @returns {boolean}
 */
export function isDate(o) {
  if (isDefined(o)) {
    return !isNaN(new Date(o).valueOf());
  }
  return false;
}

/**
 * wordCount()
 * @param s
 * @returns {number}
 * @constructor
 */
export function wordCount(s) {
  return isStringWithLength(s) ? s.split(/\b\W+\b/).length : 0;
}

export default {
  hasOwnProperty,
  init,
  isArray,
  isArrayWithLength,
  isBoolean,
  isDate,
  isDecimal,
  isDefined,
  isEmptyString,
  isFalse,
  isFunction,
  isNull,
  isNullOrUndefined,
  isNumber,
  isObject,
  isString,
  isDiff,
  isStringWithLength,
  isTrue,
  isUndefined,
  wordCount,
  camelCase
};

// function for dynamic sorting
//EXAMPLE USE:
//// array is sorted by band, in ascending order by default
////
////  const bands = [
////    { genre: 'Rap', band: 'Migos', albums: 2},
////    { genre: 'Pop', band: 'Coldplay', albums: 4, awards: 13},
////    { genre: 'Rock', band: 'Breaking Benjamins', albums: 1}
////    ];
//// bands.sort(compareValues('band', 'desc')); //DESCENDING ORDER by Band
//// bands.sort(compareValues('genre')); //ASCENDING ORDER BY Genre

export function compareValues(key, order = 'asc', ignoreCase = true) {
  return function (a, b) {
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
      // property doesn't exist on either object
      return 0;
    }

    const varA = (ignoreCase && typeof a[key] === 'string') ?
      a[key].toUpperCase() : a[key];
    const varB = (ignoreCase && typeof b[key] === 'string') ?
      b[key].toUpperCase() : b[key];

    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return (
      (order === 'desc') ? (comparison * -1) : comparison
    );
  };
}
export function compareTypedValues<t>(key, order = 'asc', ignoreCase = true) {
  return function (a: t, b: t): number {
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
      // property doesn't exist on either object
      return 0;
    }

    const varA = (ignoreCase && typeof a[key] === 'string') ?
      a[key].toUpperCase() : a[key];
    const varB = (ignoreCase && typeof b[key] === 'string') ?
      b[key].toUpperCase() : b[key];

    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return (
      (order === 'desc') ? (comparison * -1) : comparison
    );
  };
}